import React from "react";
import { jsPDF } from "jspdf";

// ── Expediente Gráfico Profesional (SVG técnico paramétrico) ──────────────────
// Planta + Elevación que se redibujan con Ancho/Largo/pisos y con el MODELO de
// fachada elegido (A/B/C), cada uno con su identidad arquitectónica. El 1er piso
// reacciona al uso dual COCHERA / TIENDA. Escala real metros→px. NO calcula presupuesto.

const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Identidad cromática/estética por modelo (según fachadas oficiales).
export const MODELS = {
  A: { nombre: "Modelo A · Inmobiliario Clásico", accent: "#1e3a8a", wall: "#f8fafc", panel: null, railing: "tube",  soffit: null,     glassTower: true },
  B: { nombre: "Modelo B · Minimalista Pro",       accent: "#334155", wall: "#ffffff", panel: "left", railing: "glass", soffit: null,     glassTower: false },
  C: { nombre: "Modelo C · Vanguardia",            accent: "#8a5a2b", wall: "#f5f3ef", panel: null, railing: "glass", soffit: "wood",   glassTower: false, frame: true },
};
export const MODEL_ACCENT = { A: MODELS.A.accent, B: MODELS.B.accent, C: MODELS.C.accent };

const INK = "#0f172a", LINE = "#334155", DIM = "#0ea5b7", WALL = "#1e293b";

const DimH = ({ x1, x2, y, text }) => (
  <g stroke={DIM} strokeWidth="0.8"><line x1={x1} y1={y} x2={x2} y2={y} /><line x1={x1} y1={y - 3} x2={x1} y2={y + 3} /><line x1={x2} y1={y - 3} x2={x2} y2={y + 3} /><text x={(x1 + x2) / 2} y={y - 3} fill={DIM} fontSize="7" fontWeight="700" textAnchor="middle" stroke="none">{text}</text></g>
);
const DimV = ({ y1, y2, x, text }) => (
  <g stroke={DIM} strokeWidth="0.8"><line x1={x} y1={y1} x2={x} y2={y2} /><line x1={x - 3} y1={y1} x2={x + 3} y2={y1} /><line x1={x - 3} y1={y2} x2={x + 3} y2={y2} /><text x={x - 4} y={(y1 + y2) / 2} fill={DIM} fontSize="7" fontWeight="700" textAnchor="middle" stroke="none" transform={`rotate(-90 ${x - 4} ${(y1 + y2) / 2})`}>{text}</text></g>
);

/* ── PLANTA TÉCNICA (1er piso · consistente con el modelo y el uso) ── */
export const PlantaTecnica = ({ ancho, largo, modeloId = "A", uso = "COCHERA" }) => {
  const A = Math.max(3, Number(ancho) || 7.5), L = Math.max(4, Number(largo) || 20);
  const VW = 420, VH = 320, mL = 46, mT = 34, mR = 26, mB = 30;
  const s = Math.min((VW - mL - mR) / A, (VH - mT - mB) / L);
  const w = A * s, h = L * s, x0 = mL, y0 = mT;
  const W25 = 0.25 * s, W15 = 0.15 * s;
  const M = MODELS[modeloId] || MODELS.A, accent = M.accent;
  const ix = x0 + W25, iy = y0 + W25, iw = w - 2 * W25, ih = h - 2 * W25;
  const stairW = Math.min(iw * 0.34, 3.0 * s);
  const rowFront = ih * 0.24, rowSocial = ih * 0.34, rowRoom = ih - rowFront - rowSocial;
  const yF = iy, yS = iy + rowFront, yR = iy + rowFront + rowSocial;
  const kitchenW = iw * 0.30, bathW = iw * 0.28;
  const doorArc = (cx, cy, r, dir = 1, rot = 0) => {
    const rad = (d) => (d * Math.PI) / 180, a1 = rot + 90 * dir;
    const p1 = [cx + r * Math.cos(rad(rot)), cy + r * Math.sin(rad(rot))], p2 = [cx + r * Math.cos(rad(a1)), cy + r * Math.sin(rad(a1))];
    return <g stroke={LINE} strokeWidth="0.7" fill="none"><line x1={cx} y1={cy} x2={p1[0]} y2={p1[1]} strokeWidth="1.3" /><path d={`M ${p1[0]} ${p1[1]} A ${r} ${r} 0 0 ${dir > 0 ? 1 : 0} ${p2[0]} ${p2[1]}`} strokeDasharray="2 2" /></g>;
  };
  const frontFill = uso === "TIENDA" ? "#dbeafe" : "#eef2f7";
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block" style={{ maxHeight: 360, background: "#fbfdff" }} role="img" aria-label="Planta técnica" data-testid="vm-planta-tecnica">
      <rect x={x0} y={y0} width={w} height={h} fill="#e2e8f0" stroke={WALL} strokeWidth="1.2" />
      <rect x={ix} y={iy} width={iw} height={ih} fill="#ffffff" stroke={WALL} strokeWidth="0.9" />
      <rect x={ix} y={yF} width={iw} height={rowFront} fill={frontFill} />
      {/* tabiquería 0.15 */}
      <rect x={ix} y={yS - W15 / 2} width={iw} height={W15} fill="#cbd5e1" />
      <rect x={ix} y={yR - W15 / 2} width={iw} height={W15} fill="#cbd5e1" />
      <rect x={ix + iw - stairW - W15 / 2} y={yS} width={W15} height={ih - rowFront} fill="#cbd5e1" />
      <rect x={ix + kitchenW - W15 / 2} y={yS} width={W15} height={rowSocial} fill="#cbd5e1" />
      <rect x={ix + iw - stairW - bathW - W15 / 2} y={yR} width={W15} height={rowRoom} fill="#cbd5e1" />
      {/* Cocina L */}
      <g stroke={accent} strokeWidth="0.8" fill="#eef6ff"><rect x={ix + 3} y={yS + 3} width={kitchenW - 8} height={7} /><rect x={ix + 3} y={yS + 3} width={7} height={rowSocial - 8} /></g>
      {/* SS.HH. */}
      <g stroke={accent} strokeWidth="0.8" fill="#eef6ff"><rect x={ix + iw - stairW - bathW + 3} y={yR + 4} width={7} height={9} rx="2" /><rect x={ix + iw - stairW - bathW + 3} y={yR + rowRoom - 10} width={9} height={5} rx="1" /><rect x={ix + iw - stairW - 12} y={yR + 4} width={8} height={8} /></g>
      {/* Escalera */}
      <g stroke={LINE} strokeWidth="0.6"><rect x={ix + iw - stairW} y={yS} width={stairW} height={ih - rowFront} fill="#f1f5f9" />{Array.from({ length: 9 }, (_, i) => <line key={i} x1={ix + iw - stairW} y1={yS + ((ih - rowFront) / 9) * (i + 1)} x2={ix + iw} y2={yS + ((ih - rowFront) / 9) * (i + 1)} />)}</g>
      {/* Puertas interiores */}
      {doorArc(ix + W15 + 4, yS - W15 / 2, 12, 1, -90)}
      {doorArc(ix + kitchenW + 6, yS + rowSocial * 0.5, 11, 1, 90)}
      {doorArc(ix + iw - stairW - bathW + 5, yR - W15 / 2, 10, 1, -90)}
      {/* FRENTE según uso: COCHERA (portón) o TIENDA (mampara + puerta comercial) */}
      {uso === "COCHERA" ? (
        <>
          <rect x={ix + 4} y={y0 + h - W25 - 1.5} width={iw * 0.52} height={2.5} fill={accent} />
          {doorArc(ix + iw - stairW - 4, y0 + h - 2, 13, -1, 180)}
        </>
      ) : (
        <>
          {/* mampara de vidrio comercial en todo el frente */}
          <rect x={ix + 4} y={y0 + h - W25 - 1} width={iw - 8} height={2.2} fill={accent} opacity="0.9" />
          {Array.from({ length: 6 }, (_, i) => <line key={i} x1={ix + 8 + i * ((iw - 12) / 6)} y1={y0 + h - W25 - 1} x2={ix + 8 + i * ((iw - 12) / 6)} y2={y0 + h - W25 + 1.2} stroke="#fff" strokeWidth="0.6" />)}
          {doorArc(ix + iw * 0.5, y0 + h - 2, 12, -1, 180)}
        </>
      )}
      {/* Rótulos */}
      <g fill={INK} fontSize="7.5" fontWeight="700" textAnchor="middle">
        <text x={ix + iw * 0.4} y={yF + rowFront * 0.55} fill={uso === "TIENDA" ? accent : INK}>{uso === "TIENDA" ? "TIENDA / LOCAL COMERCIAL" : "COCHERA / INGRESO"}</text>
        <text x={ix + kitchenW * 0.5} y={yS + rowSocial * 0.5} fill={accent}>COCINA</text>
        <text x={ix + kitchenW + (iw - kitchenW - stairW) * 0.5} y={yS + rowSocial * 0.5}>SALA · COMEDOR</text>
        <text x={ix + (iw - stairW - bathW) * 0.5} y={yR + rowRoom * 0.55}>DORMITORIO</text>
        <text x={ix + iw - stairW - bathW * 0.5} y={yR + rowRoom * 0.5} fill={accent} fontSize="6.5">SS.HH.</text>
        <text x={ix + iw - stairW * 0.5} y={yS + (ih - rowFront) * 0.5} fontSize="6.5">ESCALERA</text>
      </g>
      <DimH x1={x0} x2={x0 + w} y={y0 - 14} text={`${n2(A)}`} />
      <DimV y1={y0} y2={y0 + h} x={x0 - 16} text={`${n2(L)}`} />
      <DimV y1={yF} y2={yS} x={x0 + w + 12} text={n2(rowFront / s)} />
      <DimV y1={yS} y2={yR} x={x0 + w + 12} text={n2(rowSocial / s)} />
      <DimV y1={yR} y2={iy + ih} x={x0 + w + 12} text={n2(rowRoom / s)} />
      <text x={x0} y={VH - 8} fill="#64748b" fontSize="7" fontWeight="700">MUROS ▮0.25 carga · ▯0.15 tabique · MODELO {modeloId} · {uso} · cotas en m</text>
    </svg>
  );
};

/* ── ELEVACIÓN FRONTAL (identidad por modelo + uso dual 1er piso) ── */
export const ElevacionTecnica = ({ ancho, pisos = 1, altura1 = 3.0, alturaTipica = 2.7, parapeto = 2.7, incluirParapeto = true, modeloId = "A", uso = "COCHERA" }) => {
  const N = Math.max(1, Number(pisos) || 1), A = Math.max(3, Number(ancho) || 7.5);
  const h1 = Number(altura1) || 3.0, ht = Number(alturaTipica) || 2.7, hp = Number(parapeto) || 2.7;
  const M = MODELS[modeloId] || MODELS.A, accent = M.accent;
  const heights = [h1, ...Array.from({ length: N - 1 }, () => ht)];
  const totalH = heights.reduce((a, b) => a + b, 0) + (incluirParapeto ? hp * 0.35 : 0);
  const VW = 420, VH = 340, mL = 52, mT = 22, mB = 26, mR = 22;
  const s = Math.min((VW - mL - mR) / A, (VH - mT - mB) / totalH);
  const w = A * s, baseY = VH - mB, x0 = (VW - w) / 2 + 8;
  const npt = [0]; heights.forEach((hh) => npt.push(npt[npt.length - 1] + hh));
  const towerW = A * 0.16 * s;
  const panelW = A * 0.14 * s;
  const bodyX = M.panel === "left" ? x0 + panelW : x0;
  const bodyW = w - (M.glassTower ? towerW : 0) - (M.panel === "left" ? panelW : 0);
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block" style={{ maxHeight: 360, background: "#fbfdff" }} role="img" aria-label="Elevación frontal" data-testid="vm-elevacion-tecnica">
      <line x1={x0 - 20} y1={baseY} x2={x0 + w + 14} y2={baseY} stroke={INK} strokeWidth="1.4" />
      <rect x={x0} y={baseY - totalH * s} width={w} height={totalH * s} fill={M.wall} stroke={WALL} strokeWidth="1.2" />
      {/* B: panelado vertical gris oscuro a la izquierda */}
      {M.panel === "left" && (<g><rect x={x0} y={baseY - npt[N] * s} width={panelW} height={npt[N] * s} fill="#3f434a" />{Array.from({ length: 5 }, (_, i) => <line key={i} x1={x0 + (panelW / 5) * (i + 1)} y1={baseY - npt[N] * s} x2={x0 + (panelW / 5) * (i + 1)} y2={baseY} stroke="#2a2d32" strokeWidth="0.8" />)}</g>)}
      {/* A: torre de cristal lateral derecha */}
      {M.glassTower && (<g><rect x={x0 + w - towerW} y={baseY - npt[N] * s} width={towerW} height={npt[N] * s} fill={accent} opacity="0.92" />{Array.from({ length: Math.max(6, N * 3) }, (_, i) => <rect key={i} x={x0 + w - towerW + towerW * 0.32} y={baseY - npt[N] * s + 6 + i * 12} width={towerW * 0.36} height={5} fill="#fde68a" opacity="0.85" />)}</g>)}
      {heights.map((hh, i) => {
        const yTop = baseY - npt[i + 1] * s, yBot = baseY - npt[i] * s, isGround = i === 0;
        return (
          <g key={i}>
            <line x1={bodyX} y1={yTop} x2={bodyX + bodyW} y2={yTop} stroke={accent} strokeWidth="2.4" />
            {/* C: marco estructural blanco envolvente por nivel */}
            {M.frame && <rect x={bodyX + 3} y={yTop + 3} width={bodyW - 6} height={yBot - yTop - 6} fill="none" stroke="#ffffff" strokeWidth="3" />}
            {/* C: soffit de madera bajo la losa (techo de balcón) */}
            {M.soffit === "wood" && i >= 1 && <rect x={bodyX + 4} y={yTop} width={bodyW * 0.6} height={4} fill="#a9743e" opacity="0.9" />}
            {isGround ? (
              // 1er piso: uso dual
              uso === "COCHERA" ? (
                <g><rect x={bodyX + bodyW * 0.08} y={yBot - (yBot - yTop) * 0.62} width={bodyW * 0.5} height={(yBot - yTop) * 0.62} fill="#334155" stroke={accent} strokeWidth="0.8" />{Array.from({ length: 5 }, (_, k) => <line key={k} x1={bodyX + bodyW * 0.08} y1={yBot - (yBot - yTop) * 0.62 + ((yBot - yTop) * 0.62 / 5) * (k + 1)} x2={bodyX + bodyW * 0.58} y2={yBot - (yBot - yTop) * 0.62 + ((yBot - yTop) * 0.62 / 5) * (k + 1)} stroke="#64748b" strokeWidth="0.6" />)}<rect x={bodyX + bodyW * 0.64} y={yBot - (yBot - yTop) * 0.7} width={bodyW * 0.14} height={(yBot - yTop) * 0.7} fill="#7c4a2d" /></g>
              ) : (
                <g><rect x={bodyX + bodyW * 0.06} y={yTop + (yBot - yTop) * 0.15} width={bodyW * 0.86} height={(yBot - yTop) * 0.82} fill="#0ea5b7" opacity="0.28" stroke={accent} strokeWidth="1" />{Array.from({ length: 5 }, (_, k) => <line key={k} x1={bodyX + bodyW * 0.06 + (bodyW * 0.86 / 5) * (k + 1)} y1={yTop + (yBot - yTop) * 0.15} x2={bodyX + bodyW * 0.06 + (bodyW * 0.86 / 5) * (k + 1)} y2={yBot} stroke={accent} strokeWidth="0.6" />)}<rect x={bodyX + bodyW * 0.4} y={yBot - (yBot - yTop) * 0.55} width={bodyW * 0.16} height={(yBot - yTop) * 0.55} fill="#0f172a" opacity="0.55" /><text x={bodyX + bodyW * 0.5} y={yTop + 8} fill={accent} fontSize="6" fontWeight="800" textAnchor="middle">TIENDA</text></g>
              )
            ) : (
              <>
                <rect x={bodyX + bodyW * 0.12} y={yTop + (yBot - yTop) * 0.22} width={bodyW * 0.6} height={(yBot - yTop) * 0.5} fill="#0f172a" opacity="0.82" stroke={accent} strokeWidth="0.8" />
                {/* barandas por modelo */}
                {M.railing === "tube" ? (
                  <g stroke="#94a3b8" strokeWidth="0.8"><line x1={bodyX + 4} y1={yBot - 3} x2={bodyX + bodyW * 0.66} y2={yBot - 3} />{Array.from({ length: 9 }, (_, k) => <rect key={k} x={bodyX + 6 + k * ((bodyW * 0.62) / 9)} y={yBot - 11} width={1.6} height={8} fill="#94a3b8" stroke="none" />)}</g>
                ) : (
                  <g><rect x={bodyX + 4} y={yBot - 11} width={bodyW * 0.66} height={9} fill="#7dd3fc" opacity="0.35" stroke="#38bdf8" strokeWidth="0.6" /></g>
                )}
              </>
            )}
            <line x1={x0 - 34} y1={yBot} x2={x0} y2={yBot} stroke={DIM} strokeWidth="0.7" />
            <text x={x0 - 34} y={yBot - 2} fill={DIM} fontSize="7" fontWeight="800">N.P.T. {i === 0 ? "±0.00" : "+" + n2(npt[i])}</text>
          </g>
        );
      })}
      {incluirParapeto && <rect x={bodyX} y={baseY - totalH * s} width={bodyW} height={hp * 0.35 * s} fill="none" stroke="#94a3b8" strokeWidth="0.8" strokeDasharray="3 2" />}
      <line x1={x0 - 34} y1={baseY - npt[N] * s} x2={x0} y2={baseY - npt[N] * s} stroke={DIM} strokeWidth="0.7" />
      <text x={x0 - 34} y={baseY - npt[N] * s - 2} fill={DIM} fontSize="7" fontWeight="800">N.P.T. +{n2(npt[N])}</text>
      <DimV y1={baseY - totalH * s} y2={baseY} x={x0 + w + 12} text={`${n2(npt[N])}`} />
      <DimH x1={x0} x2={x0 + w} y={baseY + 16} text={`${n2(A)}`} />
      <text x={x0} y={mT - 8} fill={accent} fontSize="8" fontWeight="800">ELEVACIÓN · {M.nombre.toUpperCase()} · {N} NIVEL{N > 1 ? "ES" : ""} · {uso}</text>
    </svg>
  );
};

/* ── Expediente Gráfico → PDF (rasteriza los SVG en pantalla) ── */
const svgToPng = (svgEl, scale = 2) => new Promise((resolve) => {
  const xml = new XMLSerializer().serializeToString(svgEl);
  const vb = svgEl.viewBox.baseVal;
  const W = (vb && vb.width) || 420, H = (vb && vb.height) || 320;
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas"); c.width = W * scale; c.height = H * scale;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    resolve({ data: c.toDataURL("image/png"), W, H });
  };
  img.onerror = () => resolve(null);
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(xml)));
});

export async function exportExpedientePDF(p, opts = {}) {
  const { modeloId = "A", uso = "COCHERA" } = opts;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const planta = document.querySelector('[data-testid="vm-planta-tecnica"]');
  const elev = document.querySelector('[data-testid="vm-elevacion-tecnica"]');
  const pngP = planta ? await svgToPng(planta) : null;
  const pngE = elev ? await svgToPng(elev) : null;
  const M = MODELS[modeloId] || MODELS.A;
  const draw = (title, png, first) => {
    if (!first) doc.addPage("a4", "landscape");
    doc.setFillColor(15, 43, 56); doc.rect(0, 0, 297, 16, "F");
    doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text(`EXPEDIENTE GRÁFICO · ${title}`, 10, 10);
    doc.setFontSize(8); doc.text(`${M.nombre} · Uso 1er piso: ${uso} · ${p?.nombre || "VIVIENDA"}`, 200, 10);
    if (png) { const w = 250, h = w * (png.H / png.W); doc.addImage(png.data, "PNG", 23, 24, w, Math.min(h, 165)); }
    doc.setTextColor(120); doc.setFontSize(7);
    doc.text("COREX · Producción Arquitectónica · cotas en metros (2 decimales) · N.P.T. dinámicos", 10, 200);
  };
  draw("PLANTA TÉCNICA (1er piso)", pngP, true);
  draw("ELEVACIÓN FRONTAL", pngE, false);
  doc.save(`Expediente_Grafico_Modelo${modeloId}_${uso}.pdf`);
}
